import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type { ReportTraveledKilometersDto } from "@report/data/dto/report-traveled-kilometers.dto.ts";
import { ReportTraveledKilometersDtoSchema } from "@report/data/dto/report-traveled-kilometers.dto.ts";
import { Report } from "@report/domain/models/report.model";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";

@injectable()
export class ReportTraveledKilometersMapper {
    map(
        reportTraveledKilometersDto: ReportTraveledKilometersDto,
    ): Result<Report[], ModelError<typeof Report>> {
        const reportTraveledKilometersDtoParsed =
            ReportTraveledKilometersDtoSchema.safeParse(
                reportTraveledKilometersDto,
            );

        if (!reportTraveledKilometersDtoParsed.success) {
            console.error(reportTraveledKilometersDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        const reportsResults = Object.values(
            reportTraveledKilometersDto.data,
        ).map((report) => {
            const station = report.station;
            const reportPagination = report.pagination;

            const reportMapped = [
                {
                    vehicle: station.name,
                    traveledKilometers: report.data,
                },
            ];

            return Report.createWithRecovery({
                stationReportSummary: {
                    icon: station.icon,
                    stationId: station.id,
                    stationName: `${station.name} (${station.technical_name})`,
                },
                items: reportMapped,
                pagination: {
                    count: reportPagination.count,
                    current: reportPagination.current,
                    pages: reportPagination.pages,
                    perPage: reportPagination.per_page,
                },
            });
        });

        if (reportsResults.some((result) => result.isErr())) {
            return err(new NonRecuperableModelError());
        }

        return ok(
            reportsResults
                .map((result) => result.unwrapOr(null))
                .filter(Boolean),
        );
    }
}
